/** Базовая 2D-геометрия. Единицы — миллиметры. */
import { LENGTH_UNITS, formatLengthNumber, type LengthUnit } from "./units";

export interface Pt {
	x: number;
	y: number;
}

export const sub = (a: Pt, b: Pt): Pt => ({ x: a.x - b.x, y: a.y - b.y });
export const add = (a: Pt, b: Pt): Pt => ({ x: a.x + b.x, y: a.y + b.y });
export const mul = (a: Pt, k: number): Pt => ({ x: a.x * k, y: a.y * k });
export const rotateVector = (vector: Pt, angle: number): Pt => {
	const cosine = Math.cos(angle);
	const sine = Math.sin(angle);
	return { x: vector.x * cosine - vector.y * sine, y: vector.x * sine + vector.y * cosine };
};
export const len = (a: Pt): number => Math.hypot(a.x, a.y);
export const dist = (a: Pt, b: Pt): number => Math.hypot(a.x - b.x, a.y - b.y);

export const norm = (a: Pt): Pt => {
	const l = len(a);
	return l < 1e-9 ? { x: 0, y: 0 } : { x: a.x / l, y: a.y / l };
};

/** Левая нормаль к вектору. */
export const leftNormal = (a: Pt): Pt => ({ x: -a.y, y: a.x });
/** Скалярное произведение. */
export const dot = (a: Pt, b: Pt): number => a.x * b.x + a.y * b.y;

/** Угол вектора в радианах. */
export const angleOf = (a: Pt): number => Math.atan2(a.y, a.x);

/** Приводит угол к диапазону [0, 2π). */
export const normalizeAngle = (a: number): number => {
	const t = a % (Math.PI * 2);
	return t < 0 ? t + Math.PI * 2 : t;
};

/** Округление к ближайшему шагу сетки. */
export const snapTo = (v: number, step: number): number => Math.round(v / step) * step;

/**
 * Проекция точки на отрезок.
 * Возвращает параметр t (0..1), саму точку и расстояние до неё.
 */
export const projectOnSegment = (p: Pt, a: Pt, b: Pt) => {
	const ab = sub(b, a);
	const l2 = ab.x * ab.x + ab.y * ab.y;
	if (l2 < 1e-9) return { t: 0, point: a, distance: dist(p, a) };
	let t = ((p.x - a.x) * ab.x + (p.y - a.y) * ab.y) / l2;
	t = Math.max(0, Math.min(1, t));
	const point = { x: a.x + ab.x * t, y: a.y + ab.y * t };
	return { t, point, distance: dist(p, point) };
};

/** Площадь по формуле шнурования. Знак задаёт ориентацию обхода. */
export const signedArea = (poly: Pt[]): number => {
	let s = 0;
	for (let i = 0; i < poly.length; i++) {
		const p = poly[i];
		const q = poly[(i + 1) % poly.length];
		s += p.x * q.y - q.x * p.y;
	}
	return s / 2;
};

/** Центр масс многоугольника (для подписи площади внутри комнаты). */
export const centroid = (poly: Pt[]): Pt => {
	const a = signedArea(poly);
	if (Math.abs(a) < 1e-6) {
		const s = poly.reduce((acc, p) => add(acc, p), { x: 0, y: 0 });
		return mul(s, 1 / Math.max(1, poly.length));
	}
	let cx = 0;
	let cy = 0;
	for (let i = 0; i < poly.length; i++) {
		const p = poly[i];
		const q = poly[(i + 1) % poly.length];
		const cross = p.x * q.y - q.x * p.y;
		cx += (p.x + q.x) * cross;
		cy += (p.y + q.y) * cross;
	}
	return { x: cx / (6 * a), y: cy / (6 * a) };
};

/** Пересечение двух прямых, заданных точкой и направлением. null — если параллельны. */
export const lineIntersection = (p1: Pt, d1: Pt, p2: Pt, d2: Pt): Pt | null => {
	const denom = d1.x * d2.y - d1.y * d2.x;
	if (Math.abs(denom) < 1e-9) return null;
	const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
	return { x: p1.x + d1.x * t, y: p1.y + d1.y * t };
};

/**
 * Сжимает многоугольник внутрь на заданное расстояние по каждому ребру.
 * offsets[i] — насколько подвинуть ребро poly[i] -> poly[i+1].
 * Нужно, чтобы считать чистую площадь комнаты (по внутренним граням стен),
 * а не по осевым линиям.
 */
export const insetPolygon = (poly: Pt[], offsets: number[]): Pt[] => {
	const n = poly.length;
	if (n < 3) return poly;
	// Приводим к положительной ориентации, чтобы левая нормаль смотрела внутрь.
	const flipped = signedArea(poly) < 0;
	const src = flipped ? poly.toReversed() : poly;
	// При развороте ребро j соответствует исходному ребру (n-2-j).
	const off = flipped ? offsets.map((_, i) => offsets[(n - 2 - i + n) % n] ?? 0) : offsets;

	const lines = src.map((p, i) => {
		const q = src[(i + 1) % n];
		const d = norm(sub(q, p));
		const inward = leftNormal(d);
		const shift = off[i] ?? 0;
		return { point: add(p, mul(inward, shift)), dir: d };
	});

	const out: Pt[] = [];
	for (let i = 0; i < n; i++) {
		const prev = lines[(i - 1 + n) % n];
		const cur = lines[i];
		const x = lineIntersection(prev.point, prev.dir, cur.point, cur.dir);
		out.push(x ?? cur.point);
	}
	return flipped ? out.toReversed() : out;
};

/**
 * Размер на чертеже: 3450 мм → "3450". Как на рабочих чертежах — число без единицы: размерные
 * линии, зазоры, развёртки. Единица — выбранная в редакторе (`useLengthUnit`), по умолчанию мм.
 */
export const formatLength = (mm: number, unit: LengthUnit = "mm"): string =>
	formatLengthNumber(mm, unit);

/** Длина в тексте подписи: 3450 мм → "3450 мм", в метрах "3,45 м" (черновик стены, свойства). */
export const formatMillimeters = (mm: number, unit: LengthUnit = "mm"): string =>
	`${formatLengthNumber(mm, unit)} ${LENGTH_UNITS[unit].suffix}`;

/** Длина для закупки — кабель, труба: 18 400 мм → "18,40 м". */
export const formatMeters = (mm: number): string => `${(mm / 1000).toFixed(2).replace(".", ",")} м`;

/** Форматирование площади в м². */
export const formatArea = (mm2: number): string => {
	return `${(mm2 / 1e6).toFixed(2).replace(".", ",")} м²`;
};

/** Точка внутри многоугольника (алгоритм трассировки луча). */
export const pointInPolygon = (p: Pt, poly: Pt[]): boolean => {
	let inside = false;
	for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
		const a = poly[i];
		const b = poly[j];
		const intersects =
			a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
		if (intersects) inside = !inside;
	}
	return inside;
};

/** Форматирование угла в градусах: 90 -> "90°" */
export const formatAngle = (radians: number): string => {
	let deg = (radians * 180) / Math.PI;
	if (deg < 0) deg += 360;
	return `${Math.round(deg)}°`;
};

/**
 * Пересечение двух отрезков.
 * Возвращает точку и параметры t (на первом отрезке) и u (на втором),
 * либо null, если отрезки параллельны или не пересекаются в своих пределах.
 */
export const segmentIntersection = (
	p1: Pt,
	p2: Pt,
	p3: Pt,
	p4: Pt,
): { point: Pt; t: number; u: number } | null => {
	const d1 = sub(p2, p1);
	const d2 = sub(p4, p3);
	const denom = d1.x * d2.y - d1.y * d2.x;
	if (Math.abs(denom) < 1e-9) return null;

	const diff = sub(p3, p1);
	const t = (diff.x * d2.y - diff.y * d2.x) / denom;
	const u = (diff.x * d1.y - diff.y * d1.x) / denom;
	if (t < 0 || t > 1 || u < 0 || u > 1) return null;

	return { point: { x: p1.x + d1.x * t, y: p1.y + d1.y * t }, t, u };
};

/**
 * Отсекает многоугольник полуплоскостью (алгоритм Сазерленда — Ходжмена).
 * Остаётся часть, для которой (p − point) · normal ≥ 0.
 * Нужно, чтобы вырезать из контура стены кусок между двумя проёмами.
 */
export const clipPolygon = (poly: Pt[], point: Pt, normal: Pt): Pt[] => {
	if (poly.length < 3) return [];
	const side = (p: Pt) => (p.x - point.x) * normal.x + (p.y - point.y) * normal.y;

	const out: Pt[] = [];
	for (let i = 0; i < poly.length; i++) {
		const current = poly[i];
		const next = poly[(i + 1) % poly.length];
		const dCurrent = side(current);
		const dNext = side(next);

		if (dCurrent >= 0) out.push(current);
		// Ребро пересекает границу — добавляем точку пересечения
		if ((dCurrent > 0 && dNext < 0) || (dCurrent < 0 && dNext > 0)) {
			const t = dCurrent / (dCurrent - dNext);
			out.push({
				x: current.x + (next.x - current.x) * t,
				y: current.y + (next.y - current.y) * t,
			});
		}
	}
	return out;
};
